import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ArrowLeft, ArrowRight, ShieldCheck, 
  Zap, Activity, Coins, Sun, Cpu, Flame, Building, 
  Gauge, TrendingDown, Leaf, BatteryCharging, Wind, LineChart
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface EnergyCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const ENERGY_AGENT_IDS = [
  'cbam-supply-chain-carbon-auditor',
  'energy-circuit-schematic-bom-designer',
  'energy-cbam-carbon-tax-calculator',
  'energy-bess-net-billing-optimizer',
  'energy-auditor-certifier',
  'iso-50001-energy-manager',
  'esco-green-investment-analyst',
  'solar-wind-microgrid-designer',
  'utility-smart-meter-analyst',
  'oze-cppa-contract-architect',
  'eia-ovd-environmental-permit-auditor'
];

// Helper icon renderer
const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Zap className={className} />;
  return <IconComponent className={className} />;
};

export default function EnergyCenterHubView({
  onLaunchAgent,
  onBack,
  language, currency = 'UAH'}: EnergyCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [selectedSubTab, setSelectedSubTab] = useState<'all' | 'audit' | 'iso' | 'esco' | 'vde' | 'smart'>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch energy pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const allEnergyAgents = agentsConfig.filter((a: any) => ENERGY_AGENT_IDS.includes(a.id));

  const filteredAgents = allEnergyAgents.filter((agent: any) => {
    if (selectedSubTab === 'all') return true;
    if (selectedSubTab === 'audit') return agent.id === 'energy-auditor-certifier';
    if (selectedSubTab === 'iso') return agent.id === 'iso-50001-energy-manager';
    if (selectedSubTab === 'esco') return agent.id === 'esco-green-investment-analyst';
    if (selectedSubTab === 'vde') return agent.id === 'solar-wind-microgrid-designer';
    if (selectedSubTab === 'smart') return agent.id === 'utility-smart-meter-analyst';
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            {isPl ? '⚡ 5 Modułów Energetycznych & OZE' : isUk ? '⚡ 5 Енерго-Агентів Нової Генерації' : '⚡ 5 Advanced Energy Agents'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-400" />
            {isPl ? '100% ISO 50001 • OZE • Audyt Energetyczny' : isUk ? '100% ДБН • ISO 50001 • ДСТУ 4163' : '100% ISO 50001 & Energy Standards'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-amber-950 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
              {isPl ? 'AUDYT ENERGETYCZNY • ISO 50001 • ESCO • PV OZE & BESS • POMIARY SMART' : isUk ? 'ЕНЕРГОАУДИТ • ISO 50001 • ЕСКО • СЕС & MICROGRID • АСКОЕ / ЛУЗОД' : 'ENERGY AUDIT • ISO 50001 • ESCO • SOLAR & BESS • SMART METERING'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-amber-200 to-emerald-300 bg-clip-text text-transparent">
            {isPl 
              ? '⚡ CENTRUM AUDYTU ENERGETYCZNEGO & OZE' 
              : isUk 
              ? '⚡ ЦЕНТР ЕНЕРГОАУДИТУ & ЕНЕРГОМЕНЕДЖМЕНТУ' 
              : '⚡ ENERGY AUDIT & MANAGEMENT HUB'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Specjalistyczny pakiet do audytu energetycznego budynków, wdrażania ISO 50001, modelowania finansowego kontraktów ESCO, doboru farm fotowoltaicznych/wiatrowych z magazynami energii BESS oraz analityki profili obciążenia AMR/AMI.'
              : isUk
              ? 'Спеціалізований комплекс для розрахунку теплових втрат та енергетичних паспортів будівель (ДБН В.2.6-31), впровадження стандарту ISO 50001, фінансового моделювання ЕСКО-контрактів та проєктів «ЕНЕРГОДІМ»/«ГрінДІМ», розрахунку сонячних/вітрових СЕС з акумуляторами BESS у режимі Net Billing та аналізу 30-хвилинних графіків АСКОЕ.'
              : 'Dedicated AI suite for building thermal loss calculation (DBN V.2.6-31), ISO 50001 implementation, ESCO/EPC financial modeling, Net Billing solar & BESS sizing, and 30-minute AMR/AMI smart metering load profile analytics.'}
          </p>
        </div>
      </div>

      {/* Sub-Category Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        <button
          onClick={() => setSelectedSubTab('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'all'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? 'Wszyscy Agenci Energii (5)' : isUk ? 'Всі Енерго-Агенти (5)' : 'All Energy Agents (5)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('audit')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'audit'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🏢 Audyt Budynków' : isUk ? '🏢 Енергоаудит Будівель' : '🏢 Building Audit'}
        </button>

        <button
          onClick={() => setSelectedSubTab('iso')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'iso'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '📈 ISO 50001 & Przemysł' : isUk ? '📈 ISO 50001 & Промисловість' : '📈 ISO 50001 Industry'}
        </button>

        <button
          onClick={() => setSelectedSubTab('esco')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'esco'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🤝 ESCO & Dotacje' : isUk ? '🤝 ЕСКО & Гранти' : '🤝 ESCO & Grants'}
        </button>

        <button
          onClick={() => setSelectedSubTab('vde')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'vde'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '☀️ PV & Magazyny BESS' : isUk ? '☀️ СЕС & Microgrid BESS' : '☀️ Solar & Microgrid'}
        </button>

        <button
          onClick={() => setSelectedSubTab('smart')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'smart'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '⚡ Pomiary Smart AMR/AMI' : isUk ? '⚡ АСКОЕ / ЛУЗОД' : '⚡ Smart AMR/AMI'}
        </button>
      </div>

      {/* Grid of Agent Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredAgents.map((agent: any) => {
          const agentPricing = pricing[agent.id];
          const priceVal = agentPricing ? agentPricing.price : 490;
          const limitVal = agentPricing ? agentPricing.limit : 30;
          const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
          const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;

          return (
            <div
              key={agent.id}
              className="group relative bg-white border border-slate-200 hover:border-emerald-500/50 rounded-xl p-6 flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200/60 group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {getLocalizedAgentCategory(agent, language)}
                      </span>
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                        ✨ Gemini 3.7 Flash
                      </span>
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200 font-mono">
                        {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                      </span>
                      {singlePriceVal > 0 && (
                        <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                          {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mt-1.5 leading-snug">
                      {getLocalizedAgentName(agent, language)}
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-slate-600 leading-relaxed font-mono">
                  {getLocalizedAgentDesc(agent, language)}
                </p>

                {/* Features list */}
                <ul className="space-y-1.5 pt-1">
                  {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2 text-[11px] text-slate-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span className="truncate">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Launch Action */}
              <div className="pt-6 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200">
                  <Sparkles className="w-3 h-3 text-emerald-600 animate-pulse" />
                  <span>{isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}</span>
                </div>

                <button
                  onClick={() => onLaunchAgent(agent.target)}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md shadow-emerald-600/20 flex items-center gap-2 cursor-pointer hover:scale-[1.02]"
                >
                  <span>{isPl ? '🚀 Uruchom Zadanie' : isUk ? '🚀 Виконати Задачу' : '🚀 Execute Task'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
